// Korb-Packer.
//
// Der Screen, der am häufigsten benutzt wird — also muss er der schnellste sein.
// Die Echtdaten zeigen: die Körbe wiederholen sich massiv (32× "20 braune Eier L
// + 2,5 kg festkochend"). Deshalb steht die Vorlage GANZ OBEN: ein Tap füllt den
// kompletten Korb. Alles darunter ist nur für die Abweichler da.

import { useMemo, useState } from "react";
import { useProducts, type Product, INTEREST_LABELS, type InterestTag } from "@/lib/db";
import { useBasketTemplates, type BasketItem } from "@/lib/crm";
import { Button, Chip, Segmented, Sheet, Stepper, cn, euro, qtyFmt } from "@/design-system/ui";

const EGG_CHIPS = [6, 10, 20, 30, 60];

/**
 * Kartoffeln gibt es NUR im Sack. Der Hof füllt 2,5-, 5- und 12-kg-Gebinde ab —
 * "3,7 kg" existiert nicht, und der Fahrer kann nichts abwiegen. Deshalb gibt es
 * hier keine Kilo-Eingabe mehr, sondern drei Zähler. Krumme Zwischenmengen sind
 * schon in der Eingabe unmöglich.
 */
const SACKGROESSEN = [2.5, 5, 12] as const;
type Saecke = Record<string, number>;

/** 2.5 → "2500" (Teil der Artikelnummer KA-F-2500). */
const sackKey = (kg: number) => String(Math.round(kg * 1000));
const sackLabel = (kg: number) => `${qtyFmt(kg)} kg`;

type EggColor = "B" | "W";
type EggSize = "M" | "L" | "XL";
type PotatoKind = "F" | "W";

export interface BasketDraft {
  items: BasketItem[];
  weekdays: number[];
  intervalWeeks: number;
  interestTags: InterestTag[];
  preferredTime: string | null;
}

/** Items → UI-Zustand zurückrechnen, damit Bearbeiten funktioniert. */
function decode(items: BasketItem[], products: Product[]) {
  const bySku = new Map(products.map((p) => [p.id, p.sku ?? ""]));
  let eggQty = 0;
  let eggColor: EggColor = "B";
  let eggSize: EggSize = "L";
  let potatoKind: PotatoKind = "F";
  const saecke: Saecke = { "2500": 0, "5000": 0, "12000": 0 };
  const extras: BasketItem[] = [];

  for (const it of items) {
    const sku = bySku.get(it.product_id) ?? "";
    const egg = sku.match(/^EI-(B|W)-(M|L|XL)$/);
    const sack = sku.match(/^KA-(F|W)-(\d+)$/);

    if (egg) {
      eggQty = it.qty;
      eggColor = egg[1] as EggColor;
      eggSize = egg[2] as EggSize;
    } else if (sack) {
      potatoKind = sack[1] as PotatoKind;
      saecke[sack[2]] = (saecke[sack[2]] ?? 0) + it.qty;
    } else {
      extras.push(it);
    }
  }
  return { eggQty, eggColor, eggSize, potatoKind, saecke, extras };
}

export function BasketSheet({
  open,
  onClose,
  initial,
  onSave,
  saving,
}: {
  open: boolean;
  onClose: () => void;
  initial: BasketDraft;
  onSave: (d: BasketDraft) => void;
  saving?: boolean;
}) {
  const { data: products = [], isLoading: productsLoading } = useProducts({ onlyActive: true });
  const { data: templates = [] } = useBasketTemplates();

  // Ohne Produkte lässt sich der bestehende Korb nicht dekodieren — er sähe leer
  // aus, und ein Speichern würde ihn tatsächlich leeren. Also erst gar nicht
  // bedienbar machen, bis die Produkte da sind.
  const productsReady = !productsLoading && products.length > 0;

  const skuToId = useMemo(
    () => new Map(products.map((p) => [p.sku ?? "", p.id])),
    [products]
  );
  const priceOf = (sku: string) =>
    products.find((p) => p.sku === sku)?.price_cents ?? 0;

  const dec = useMemo(() => decode(initial.items, products), [initial.items, products]);

  const [eggQty, setEggQty] = useState(dec.eggQty);
  const [eggColor, setEggColor] = useState<EggColor>(dec.eggColor);
  const [eggSize, setEggSize] = useState<EggSize>(dec.eggSize);
  const [potatoKind, setPotatoKind] = useState<PotatoKind>(dec.potatoKind);
  const [saecke, setSaecke] = useState<Saecke>(dec.saecke);
  const [weekdays, setWeekdays] = useState<number[]>(initial.weekdays);
  const [intervalWeeks, setIntervalWeeks] = useState(initial.intervalWeeks);
  const [tags, setTags] = useState<InterestTag[]>(initial.interestTags);

  const eggSku = `EI-${eggColor}-${eggSize}`;
  const sackSku = (kg: number) => `KA-${potatoKind}-${sackKey(kg)}`;

  const setSack = (kg: number, anzahl: number) =>
    setSaecke((s) => ({ ...s, [sackKey(kg)]: Math.max(0, anzahl) }));

  const kartoffelKg = SACKGROESSEN.reduce(
    (s, kg) => s + (saecke[sackKey(kg)] ?? 0) * kg,
    0
  );
  const kartoffelPreis = SACKGROESSEN.reduce(
    (s, kg) => s + (saecke[sackKey(kg)] ?? 0) * priceOf(sackSku(kg)),
    0
  );

  const total = eggQty * priceOf(eggSku) + kartoffelPreis;

  const applyTemplate = (items: (BasketItem & { sku?: string })[]) => {
    const d = decode(items, products);
    setEggQty(d.eggQty);
    setEggColor(d.eggColor);
    setEggSize(d.eggSize);
    setPotatoKind(d.potatoKind);
    setSaecke(d.saecke);
  };

  const buildItems = (): BasketItem[] => {
    const out: BasketItem[] = [];
    if (eggQty > 0 && skuToId.get(eggSku)) {
      out.push({ product_id: skuToId.get(eggSku)!, qty: eggQty });
    }
    // Je Sackgröße eine eigene Position — genau so, wie sie geladen und berechnet
    // wird. Die Packliste zählt dadurch von selbst Säcke statt Kilo.
    for (const kg of SACKGROESSEN) {
      const anzahl = saecke[sackKey(kg)] ?? 0;
      const id = skuToId.get(sackSku(kg));
      if (anzahl > 0 && id) out.push({ product_id: id, qty: anzahl });
    }
    // Positionen, die dieser Packer nicht bearbeitet (z.B. ein Nebenartikel, den
    // das Büro von Hand ins Abo gelegt hat), MÜSSEN erhalten bleiben. Sonst wäre
    // "Korb öffnen und ohne Änderung speichern" ein stiller Datenverlust.
    out.push(...dec.extras);
    return out;
  };

  // Nächste drei Liefertermine — der Bauer soll den Turnus SEHEN, nicht erraten.
  const nextDates = useMemo(
    () => previewDates(weekdays, intervalWeeks, 3),
    [weekdays, intervalWeeks]
  );

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Korb packen"
      footer={
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-wider text-ink-400">pro Lieferung</div>
            <div className="text-xl font-semibold tabular-nums text-ink-50">{euro(total)}</div>
          </div>
          <Button
            variant="primary"
            size="lg"
            loading={saving || !productsReady}
            disabled={!productsReady}
            onClick={() =>
              onSave({
                items: buildItems(),
                weekdays,
                intervalWeeks,
                interestTags: tags,
                preferredTime: initial.preferredTime,
              })
            }
          >
            Speichern
          </Button>
        </div>
      }
    >
      <div className="space-y-7">
        {/* ---- Vorlagen: der schnellste Weg ----
             Bewusst EINE wischbare Zeile statt Umbruch: sieben Chips im Umbruch
             füllen den halben Bildschirm, und der Nutzer müsste erst scrollen,
             um überhaupt zu den Eiern zu kommen. */}
        {templates.length > 0 && (
          <section>
            <SectionLabel>Vorlage</SectionLabel>
            <div className="-mx-5 flex snap-x snap-mandatory gap-2 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {templates.map((t) => (
                <Chip
                  key={t.id}
                  onClick={() => applyTemplate(t.items)}
                  className="shrink-0 snap-start whitespace-nowrap"
                >
                  {t.name}
                  <span className="text-ink-500">·{t.use_count}</span>
                </Chip>
              ))}
            </div>
          </section>
        )}

        {/* ---- Eier ---- */}
        <section>
          <SectionLabel>Eier</SectionLabel>
          {/* Die fünf gängigen Mengen in EINER Reihe — sie decken die große
              Mehrheit der Körbe ab; der Stepper darunter ist für den Rest. */}
          <div className="mb-3 grid grid-cols-5 gap-2">
            {EGG_CHIPS.map((n) => (
              <Chip
                key={n}
                active={eggQty === n}
                onClick={() => setEggQty(n)}
                className="justify-center px-0"
              >
                {n}
              </Chip>
            ))}
          </div>

          <div className="mb-3">
            <Stepper value={eggQty} onChange={setEggQty} step={eggQty >= 10 ? 10 : 1} max={500} suffix="Stück" />
          </div>

          {eggQty > 0 && (
            <div className="grid grid-cols-2 gap-2">
              <Segmented
                value={eggColor}
                onChange={setEggColor}
                options={[
                  { value: "B", label: "Braun" },
                  { value: "W", label: "Weiß" },
                ]}
              />
              <Segmented
                value={eggSize}
                onChange={setEggSize}
                options={[
                  { value: "M", label: "M" },
                  { value: "L", label: "L" },
                  { value: "XL", label: "XL" },
                ]}
              />
            </div>
          )}
        </section>

        {/* ---- Kartoffeln: nur ganze Säcke ---- */}
        <section>
          <SectionLabel>Kartoffeln</SectionLabel>

          <div className="mb-3 space-y-2">
            {SACKGROESSEN.map((kg) => {
              const anzahl = saecke[sackKey(kg)] ?? 0;
              return (
                <div
                  key={kg}
                  className={cn(
                    "flex items-center justify-between gap-3 rounded-lg border px-3 py-2",
                    "transition-colors duration-fast",
                    anzahl > 0
                      ? "border-gruen-500/40 bg-gruen-500/5"
                      : "border-ink-700 bg-ink-900"
                  )}
                >
                  <div className="min-w-0">
                    <div className="text-[15px] font-medium text-ink-100">
                      Sack {sackLabel(kg)}
                    </div>
                    <div className="text-xs tabular-nums text-ink-400">
                      {euro(priceOf(sackSku(kg)))} je Sack
                    </div>
                  </div>

                  <Stepper
                    value={anzahl}
                    onChange={(v) => setSack(kg, v)}
                    step={1}
                    max={40}
                    suffix="×"
                  />
                </div>
              );
            })}
          </div>

          {kartoffelKg > 0 && (
            <>
              <p className="mb-3 text-sm tabular-nums text-ink-400">
                Gesamt {qtyFmt(kartoffelKg)} kg · {euro(kartoffelPreis)}
              </p>
              <Segmented
                value={potatoKind}
                onChange={setPotatoKind}
                options={[
                  { value: "F", label: "festkochend" },
                  { value: "W", label: "weichkochend" },
                ]}
              />
            </>
          )}
        </section>

        {/* ---- Turnus ---- */}
        <section>
          <SectionLabel>Liefertage</SectionLabel>
          <div className="mb-3 flex gap-1.5">
            {[1, 2, 3, 4, 5, 6].map((d) => {
              const active = weekdays.includes(d);
              return (
                <button
                  key={d}
                  onClick={() =>
                    setWeekdays((w) =>
                      w.includes(d) ? w.filter((x) => x !== d) : [...w, d].sort()
                    )
                  }
                  className={cn(
                    "h-11 flex-1 rounded-lg border text-sm font-medium transition-colors duration-fast",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
                    active
                      ? "border-gruen-500/50 bg-gruen-500/15 text-gruen-400"
                      : "border-ink-700 bg-ink-900 text-ink-400 hover:text-ink-200"
                  )}
                >
                  {["Mo", "Di", "Mi", "Do", "Fr", "Sa"][d - 1]}
                </button>
              );
            })}
          </div>

          <Segmented
            value={intervalWeeks}
            onChange={setIntervalWeeks}
            options={[
              { value: 1, label: "jede Woche" },
              { value: 2, label: "alle 2" },
              { value: 4, label: "alle 4" },
            ]}
          />

          {nextDates.length > 0 && (
            <p className="mt-2.5 text-sm text-ink-400">
              Nächste Lieferungen:{" "}
              <span className="tabular-nums text-ink-200">{nextDates.join("  ·  ")}</span>
            </p>
          )}
        </section>

        {/* ---- Verkaufs-Marker ---- */}
        <section>
          <SectionLabel>Nimmt gern mit</SectionLabel>
          <p className="mb-2.5 text-sm text-ink-500">
            Kein fester Korbinhalt — der Fahrer sieht es am Stop und bietet es an.
          </p>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(INTEREST_LABELS) as InterestTag[]).map((t) => (
              <Chip
                key={t}
                active={tags.includes(t)}
                onClick={() =>
                  setTags((v) => (v.includes(t) ? v.filter((x) => x !== t) : [...v, t]))
                }
              >
                {INTEREST_LABELS[t]}
              </Chip>
            ))}
          </div>
        </section>

        {/* ---- Zusammenfassung ---- */}
        {(eggQty > 0 || kartoffelKg > 0) && (
          <section className="rounded-xl border border-ink-700/70 bg-ink-850 p-4">
            <SectionLabel>Korb</SectionLabel>
            <ul className="space-y-1.5 text-[15px]">
              {eggQty > 0 && (
                <SummaryRow
                  label={`${eggQty}× Eier ${eggColor === "B" ? "braun" : "weiß"} ${eggSize}`}
                  value={euro(eggQty * priceOf(eggSku))}
                />
              )}

              {/* Jede Sackgröße einzeln — der Fahrer muss wissen, WELCHEN Sack er
                  greift, nicht wie viele Kilo das insgesamt sind. */}
              {SACKGROESSEN.map((kg) => {
                const anzahl = saecke[sackKey(kg)] ?? 0;
                if (!anzahl) return null;
                return (
                  <SummaryRow
                    key={kg}
                    label={`${anzahl}× Sack ${sackLabel(kg)} ${potatoKind === "F" ? "festkochend" : "weichkochend"}`}
                    value={euro(anzahl * priceOf(sackSku(kg)))}
                  />
                );
              })}

              {kartoffelKg > 0 && (
                <li className="border-t border-ink-700/70 pt-1.5 text-sm tabular-nums text-ink-400">
                  {qtyFmt(kartoffelKg)} kg Kartoffeln insgesamt
                </li>
              )}
            </ul>
          </section>
        )}
      </div>
    </Sheet>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2.5 text-xs font-medium uppercase tracking-wider text-ink-400">
      {children}
    </h3>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex items-baseline justify-between gap-3">
      <span className="min-w-0 truncate text-ink-200">{label}</span>
      <span className="shrink-0 tabular-nums text-ink-100">{value}</span>
    </li>
  );
}

/** Die nächsten N Liefertermine aus Wochentagen + Turnus. */
function previewDates(weekdays: number[], intervalWeeks: number, count: number): string[] {
  if (!weekdays.length) return [];
  const out: string[] = [];
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const anchor = new Date(start);

  for (let i = 1; i <= 120 && out.length < count; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const iso = ((d.getDay() + 6) % 7) + 1; // 1=Mo … 7=So
    if (!weekdays.includes(iso)) continue;

    const weeksSince = Math.floor((d.getTime() - anchor.getTime()) / (7 * 86400000));
    if (weeksSince % intervalWeeks !== 0) continue;

    out.push(
      d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })
    );
  }
  return out;
}
